import * as React from 'react';

import { useMemberSession } from '@/auth/auth-provider';

import { DiscoverPage } from '../domains/event-discovery/discover-page';

import { WelcomeHero } from './welcome-hero';

/**
 * What `/app` renders — the front door for a visitor, Discover for a member (`MEM-046`).
 *
 * ## Why this file exists rather than a condition inside Discover
 *
 * `MEM-014` AC-1 pins Discover's order down to the element: *"a greeting, a search field, an
 * atmosphere filter row, and a list of published events"*. A hero rendered from inside that
 * component would be a fifth thing ahead of the four the criterion names, in the one file whose
 * job is to be exactly those four. Composing here keeps `discover-page.tsx` answering only to its
 * own story, and keeps the "is anybody signed in" question out of a screen that does not
 * otherwise care.
 *
 * ## The three states, and why `loading` is not one of the two
 *
 * `useMemberSession` reports `loading` before the session is known. Treating that as signed-out
 * would flash the full-screen gradient at a returning member on every cold start — the worst
 * possible frame to show somebody walking into an event. So the hero renders on
 * `unauthenticated` alone, and the other two states render Discover, which is what a member sees
 * anyway and what a visitor is about to scroll to.
 *
 * That does mean a visitor sees Discover for the moment the session request is in flight. It is
 * the right way round: the cost is a hero that arrives a beat late, rather than a member being
 * shown a marketing screen for the app they have already joined.
 */
export function MemberHomePage(): React.ReactElement {
  const { status } = useMemberSession();

  return (
    <>
      {status === 'unauthenticated' ? <WelcomeHero /> : null}
      {/* Unconditional. `MEM-046` AC-4: the hero sits above Discover, never in place of it. */}
      <DiscoverPage />
    </>
  );
}
